import React, { useState, useEffect } from 'react';
import { 
  ShieldCheck, Lock, FileText, Cookie, Scale, 
  CheckCircle2, ArrowLeft, Printer, ExternalLink, 
  Download, Calendar, Building2, Mail, Phone, MapPin, 
  AlertCircle, Check, Sparkles, ChevronRight, Sliders, RefreshCw
} from 'lucide-react';
import { INSTITUTION_INFO } from '../../data/portalData';

export const LegalPage = ({ 
  initialTab = 'privacy', // 'privacy' | 'terms' | 'cookies'
  setActiveView 
}) => {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [cookiePreferences, setCookiePreferences] = useState({
    essential: true, // Always required
    analytics: true,
    functional: true,
    marketing: false
  });
  const [cookieSaved, setCookieSaved] = useState(false);

  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeTab]);

  const handlePrint = () => {
    window.print();
  };

  const handleSaveCookiePreferences = () => {
    setCookieSaved(true);
    setTimeout(() => setCookieSaved(false), 3000);
  };

  return (
    <div className="bg-slate-50 min-h-screen py-10 px-4 sm:px-6 lg:px-8 text-slate-800">
      <div className="max-w-6xl mx-auto space-y-8">
        
        {/* Breadcrumb & Navigation Controls */}
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-200 pb-4 print:hidden">
          <button
            onClick={() => {
              if (setActiveView) setActiveView('home');
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            className="flex items-center space-x-2 text-xs font-bold text-slate-600 hover:text-[#7A470E] transition-colors cursor-pointer font-sc"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Return to Main Portal</span>
          </button>

          <div className="flex items-center space-x-3 text-xs">
            <span className="text-slate-400">Effective Date: 1st October 2026</span>
            <span className="text-slate-300">•</span>
            <button
              onClick={handlePrint}
              className="px-3 py-1.5 rounded-lg bg-white border border-slate-300 text-slate-700 hover:text-[#7A470E] hover:border-[#7A470E] font-bold transition-all shadow-xs flex items-center space-x-1.5 cursor-pointer font-sc"
            >
              <Printer className="w-3.5 h-3.5" />
              <span>Print Official Document</span>
            </button>
          </div>
        </div>

        {/* Master Institutional Header Banner */}
        <div className="bg-gradient-to-r from-[#030F1C] via-[#06182B] to-[#0B2644] text-white rounded-3xl p-6 sm:p-10 border-2 border-[#7A470E] shadow-xl relative overflow-hidden">
          <div className="absolute right-0 top-0 w-80 h-80 bg-[#7A470E]/15 rounded-full blur-3xl pointer-events-none" />

          <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="space-y-2">
              <div className="flex items-center space-x-2.5">
                <span className="px-3 py-0.5 rounded-full bg-[#7A470E] text-amber-100 text-[10px] font-black uppercase tracking-widest font-sc">
                  Statutory University Policies
                </span>
                <span className="text-slate-400 text-xs font-mono">NDPA 2023 · NUC Approved</span>
              </div>
              <h1 className="text-2xl sm:text-4xl font-black font-sc text-white tracking-wide">
                Institutional Compliance & Legal Directives
              </h1>
              <p className="text-xs sm:text-sm text-slate-200 max-w-2xl leading-relaxed">
                Akwa Ibom State University Business School enforces absolute fidelity to academic integrity, 
                executive data confidentiality under the Nigeria Data Protection Act (NDPA) 2023, and transparent portal operation.
              </p>
            </div>

            {/* University Seal Badge */}
            <div className="hidden lg:flex flex-col items-center bg-white/5 border border-white/10 p-4 rounded-2xl text-center shrink-0">
              <img src="/Logo.png" alt="AKSU Seal" className="w-14 h-14 object-contain mb-2 logo-glow select-none" />
              <span className="text-[11px] font-black font-sc text-[#B87A28] uppercase">Office of the Registrar</span>
              <span className="text-[9px] text-slate-300">Senate Governance Board</span>
            </div>
          </div>

          {/* Document Switcher Tabs */}
          <div className="mt-8 pt-6 border-t border-slate-800 grid grid-cols-1 sm:grid-cols-3 gap-2.5 text-xs print:hidden">
            <button
              onClick={() => setActiveTab('privacy')}
              className={`p-3 rounded-xl flex items-center space-x-3 transition-all cursor-pointer font-sc ${
                activeTab === 'privacy'
                  ? 'bg-[#7A470E] text-white font-black shadow-md'
                  : 'bg-white/5 hover:bg-white/10 text-slate-300 hover:text-white'
              }`}
            >
              <ShieldCheck className="w-4 h-4 text-amber-300 shrink-0" />
              <div className="text-left">
                <div className="font-bold">Privacy Policy</div>
                <div className="text-[10px] opacity-80">NDPA 2023 & Data Rights</div>
              </div>
            </button>

            <button
              onClick={() => setActiveTab('terms')}
              className={`p-3 rounded-xl flex items-center space-x-3 transition-all cursor-pointer font-sc ${
                activeTab === 'terms'
                  ? 'bg-[#7A470E] text-white font-black shadow-md'
                  : 'bg-white/5 hover:bg-white/10 text-slate-300 hover:text-white'
              }`}
            >
              <Scale className="w-4 h-4 text-amber-300 shrink-0" />
              <div className="text-left">
                <div className="font-bold">Site Terms of Use</div>
                <div className="text-[10px] opacity-80">Academic Portal Licensing</div>
              </div>
            </button>

            <button
              onClick={() => setActiveTab('cookies')}
              className={`p-3 rounded-xl flex items-center space-x-3 transition-all cursor-pointer font-sc ${
                activeTab === 'cookies'
                  ? 'bg-[#7A470E] text-white font-black shadow-md'
                  : 'bg-white/5 hover:bg-white/10 text-slate-300 hover:text-white'
              }`}
            >
              <Cookie className="w-4 h-4 text-amber-300 shrink-0" />
              <div className="text-left">
                <div className="font-bold">Cookie Note & Settings</div>
                <div className="text-[10px] opacity-80">Tracking & Consent Choices</div>
              </div>
            </button>
          </div>
        </div>

        {/* ======================================================== */}
        {/* DOCUMENT 1: PRIVACY POLICY                               */}
        {/* ======================================================== */}
        {activeTab === 'privacy' && (
          <div className="bg-white rounded-3xl p-6 sm:p-10 border-2 border-slate-200 shadow-md space-y-8 animate-in fade-in duration-200">
            
            <div className="border-b-2 border-slate-100 pb-5">
              <span className="text-xs font-black uppercase tracking-wider text-[#7A470E] font-sc">
                Official Senate Data Directive
              </span>
              <h2 className="text-2xl sm:text-3xl font-black font-sc text-[#0B2644] mt-1">
                Executive Privacy Policy & Scholar Data Protection Notice
              </h2>
              <p className="text-xs text-slate-500 mt-1">
                Compliant with the Nigeria Data Protection Act (NDPA) 2023, National Universities Commission (NUC) Directives, and International Standards.
              </p>
            </div>

            {/* Quick Summary Pill Highlights */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 p-4 rounded-2xl bg-amber-50/50 border border-amber-200 text-xs">
              <div className="flex items-start space-x-2">
                <CheckCircle2 className="w-4 h-4 text-[#7A470E] shrink-0 mt-0.5" />
                <div>
                  <strong className="block text-slate-900 font-sc">Statutory Data Controller</strong>
                  <span className="text-slate-600 text-[11px]">Akwa Ibom State University Senate & Directorate of Business School</span>
                </div>
              </div>
              <div className="flex items-start space-x-2">
                <Lock className="w-4 h-4 text-[#7A470E] shrink-0 mt-0.5" />
                <div>
                  <strong className="block text-slate-900 font-sc">PCI-DSS Payment Security</strong>
                  <span className="text-slate-600 text-[11px]">Zero card data retained on university servers; encrypted via Paystack</span>
                </div>
              </div>
              <div className="flex items-start space-x-2">
                <ShieldCheck className="w-4 h-4 text-[#7A470E] shrink-0 mt-0.5" />
                <div>
                  <strong className="block text-slate-900 font-sc">DPO Oversight</strong>
                  <span className="text-slate-600 text-[11px]">Direct legal recourse to Designated University Data Protection Officer</span>
                </div>
              </div>
            </div>

            {/* Content Sections */}
            <div className="space-y-6 text-xs sm:text-sm text-slate-700 leading-relaxed">
              
              <section className="space-y-2">
                <h3 className="text-base font-black font-sc text-[#0B2644] border-l-4 border-[#7A470E] pl-3">
                  1. Scope and Legal Mandate
                </h3>
                <p>
                  This Privacy Policy applies to all prospective applicants, enrolled executive scholars (PhD, DBA, MBA, EMPA, EMAF, and PGD candidates), 
                  visiting faculty, corporate fellows, and alumni accessing the official web portal (<strong>businessschool.aksu.edu.ng</strong>) and its 
                  associated Learning Management System (LMS). Akwa Ibom State University processes personal information under lawful mandates established by 
                  the <em>Akwa Ibom State University Law</em>, the <em>National Universities Commission (NUC) Guidelines</em>, and the <em>Nigeria Data Protection Act (NDPA) 2023</em>.
                </p>
              </section>

              <section className="space-y-2">
                <h3 className="text-base font-black font-sc text-[#0B2644] border-l-4 border-[#7A470E] pl-3">
                  2. Personal and Professional Information Collected
                </h3>
                <p>
                  In pursuit of academic verification and executive matriculation, the Business School collects and processes:
                </p>
                <ul className="list-disc pl-5 space-y-1 text-slate-600">
                  <li><strong>Biographical & Identity Data:</strong> Full legal name, title, surname, first name, middle name, name as specified for graduation certificates, gender, date of birth, passport photograph, state of origin, local government area (LGA), and international citizenship data where applicable.</li>
                  <li><strong>Academic Records & Screening Dossiers:</strong> First-degree and master’s degree certificates, certified academic transcripts, National Youth Service Corps (NYSC) discharge/exemption certificates, research topic synopses, and publication listings.</li>
                  <li><strong>Corporate & Professional Credentials:</strong> Current employer, MDA or corporate entity, executive rank or job designation, verified years of post-qualification experience, and professional body fellowships (e.g., ICAN, NIM, CIBN, CITN, IoD).</li>
                  <li><strong>Financial & Payment Records:</strong> Paystack transaction reference keys, payment dates, statutory processing fees (₦25,000 application fee and ₦50,000 acceptance fee), and tuition clearance records. <em>Credit and debit card numbers, expiration dates, and CVVs are processed directly by Paystack's PCI-DSS Level 1 certified gateway and are never retained on university infrastructure.</em></li>
                  <li><strong>LMS Telemetry & Academic Progression:</strong> Courseware viewing timestamps, video masterclass progress, Silicon AI tutoring interaction logs, continuous assessment (CA) submissions, and grading records.</li>
                </ul>
              </section>

              <section className="space-y-2">
                <h3 className="text-base font-black font-sc text-[#0B2644] border-l-4 border-[#7A470E] pl-3">
                  3. Lawful Bases for Data Processing
                </h3>
                <p>
                  Every processing operation conducted by the Business School relies upon strict lawful bases under Section 25 of the NDPA 2023:
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                  <div className="p-3 bg-slate-50 rounded-xl border border-slate-200">
                    <strong className="text-slate-900 block font-sc">a. Contractual Educational Obligation:</strong>
                    <span>Processing required to review admissions dossiers, administer coursework, conduct examinations, and confer accredited postgraduate degrees.</span>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-xl border border-slate-200">
                    <strong className="text-slate-900 block font-sc">b. Statutory Legal Compliance:</strong>
                    <span>Mandatory annual reporting to the National Universities Commission (NUC), Joint Admissions and Matriculation Board (JAMB), and state education ministries.</span>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-xl border border-slate-200">
                    <strong className="text-slate-900 block font-sc">c. Legitimate Institutional Interest:</strong>
                    <span>Enhancement of the Silicon AI learning model, cybersecurity audit logging, anti-plagiarism verification via Turnitin, and alumni network tracking.</span>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-xl border border-slate-200">
                    <strong className="text-slate-900 block font-sc">d. Explicit Consent:</strong>
                    <span>Voluntary participation in student surveys, marketing features, and international academic exchange partnerships.</span>
                  </div>
                </div>
              </section>

              <section className="space-y-2">
                <h3 className="text-base font-black font-sc text-[#0B2644] border-l-4 border-[#7A470E] pl-3">
                  4. Artificial Intelligence & Silicon Learning Assistant Transparency
                </h3>
                <p>
                  The integrated <strong>Silicon AI Assistant</strong> serves as an academic and methodological study companion. 
                  Queries submitted to Silicon are evaluated solely to provide instant contextual guidance on Saunders' Research Onion, 
                  epistemological frameworks, and APA 7th referencing. 
                  <strong>No automated admission denial or disciplinary expulsion is executed solely through artificial intelligence algorithms</strong>. 
                  All academic evaluations remain under the direct review of the Admissions Screening Committee, assigned Faculty Supervisors, 
                  and the University Senate.
                </p>
              </section>

              <section className="space-y-2">
                <h3 className="text-base font-black font-sc text-[#0B2644] border-l-4 border-[#7A470E] pl-3">
                  5. Data Retention & Archival Directives
                </h3>
                <p>
                  Official academic degrees, Senate award gazettes, and matriculation transcripts are retained permanently within the 
                  University Senate Archives to preserve lifetime degree verification for alumni and corporate employers. 
                  Unsuccessful applicant dossiers are archived for three (3) academic sessions to support re-application audits, 
                  after which digital records are purged securely in compliance with the university's data destruction protocols.
                </p>
              </section>

              <section className="space-y-2">
                <h3 className="text-base font-black font-sc text-[#0B2644] border-l-4 border-[#7A470E] pl-3">
                  6. Statutory Rights of Executive Scholars (NDPA 2023)
                </h3>
                <p>
                  Under Nigerian data protection legislation, you are endowed with enforceable statutory rights:
                </p>
                <ul className="list-disc pl-5 space-y-1 text-slate-600">
                  <li><strong>Right to Rectification:</strong> You may correct inaccurate biographic, contact, or corporate designation details through the Student Portal or Admissions Desk.</li>
                  <li><strong>Right to Access:</strong> You may request a verified transcript copy of all data processed concerning your candidacy or student profile.</li>
                  <li><strong>Right to Object:</strong> You may object to non-statutory data processing, including marketing publications and research surveys.</li>
                  <li><strong>Right to Lodge a Complaint:</strong> If you believe your data has been handled inconsistently with the law, you have the right to petition the Nigeria Data Protection Commission (NDPC).</li>
                </ul>
              </section>

              <section className="space-y-3 pt-4 border-t border-slate-200">
                <h3 className="text-base font-black font-sc text-[#0B2644] border-l-4 border-[#7A470E] pl-3">
                  7. Data Protection Officer (DPO) Contact
                </h3>
                <p>
                  For formal data access inquiries, consent revocations, or compliance questions, please contact:
                </p>
                <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200 flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <strong className="block text-slate-900 font-sc text-sm">Data Protection Officer & Registrar</strong>
                    <span className="text-slate-600 block text-xs">Directorate of Legal Affairs & Information Governance</span>
                    <span className="text-slate-500 block text-xs">Akwa Ibom State University, Main Campus, Ikot Akpaden</span>
                  </div>
                  <div className="text-xs space-y-1">
                    <div className="flex items-center space-x-2 text-slate-700">
                      <Mail className="w-3.5 h-3.5 text-[#7A470E]" />
                      <span className="font-mono">dpo@businessschool.aksu.edu.ng</span>
                    </div>
                    <div className="flex items-center space-x-2 text-slate-700">
                      <Phone className="w-3.5 h-3.5 text-[#7A470E]" />
                      <span>{INSTITUTION_INFO.contacts.phoneWhatsApp}</span>
                    </div>
                  </div>
                </div>
              </section>

            </div>
          </div>
        )}

        {/* ======================================================== */}
        {/* DOCUMENT 2: TERMS OF USE                                 */}
        {/* ======================================================== */}
        {activeTab === 'terms' && (
          <div className="bg-white rounded-3xl p-6 sm:p-10 border-2 border-slate-200 shadow-md space-y-8 animate-in fade-in duration-200">
            
            <div className="border-b-2 border-slate-100 pb-5">
              <span className="text-xs font-black uppercase tracking-wider text-[#7A470E] font-sc">
                Official Academic Contract
              </span>
              <h2 className="text-2xl sm:text-3xl font-black font-sc text-[#0B2644] mt-1">
                Portal Terms of Use & Executive Academic Agreement
              </h2>
              <p className="text-xs text-slate-500 mt-1">
                Binding Terms Governing Admission Applications, LMS Courseware Access, and Digital University Interactions.
              </p>
            </div>

            {/* Quick Summary Pill Highlights */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 p-4 rounded-2xl bg-amber-50/50 border border-amber-200 text-xs">
              <div className="flex items-start space-x-2">
                <Scale className="w-4 h-4 text-[#7A470E] shrink-0 mt-0.5" />
                <div>
                  <strong className="block text-slate-900 font-sc">Senate Authority</strong>
                  <span className="text-slate-600 text-[11px]">Binding university statutes under Laws of Akwa Ibom State</span>
                </div>
              </div>
              <div className="flex items-start space-x-2">
                <FileText className="w-4 h-4 text-[#7A470E] shrink-0 mt-0.5" />
                <div>
                  <strong className="block text-slate-900 font-sc">Single-User Licensing</strong>
                  <span className="text-slate-600 text-[11px]">Strictly non-transferable student login credentials</span>
                </div>
              </div>
              <div className="flex items-start space-x-2">
                <CheckCircle2 className="w-4 h-4 text-[#7A470E] shrink-0 mt-0.5" />
                <div>
                  <strong className="block text-slate-900 font-sc">Turnitin & Integrity Code</strong>
                  <span className="text-slate-600 text-[11px]">Compulsory anti-plagiarism threshold under 15%</span>
                </div>
              </div>
            </div>

            {/* Content Sections */}
            <div className="space-y-6 text-xs sm:text-sm text-slate-700 leading-relaxed">
              
              <section className="space-y-2">
                <h3 className="text-base font-black font-sc text-[#0B2644] border-l-4 border-[#7A470E] pl-3">
                  1. Acceptance of Terms
                </h3>
                <p>
                  By accessing the Akwa Ibom State University Business School portal (<strong>businessschool.aksu.edu.ng</strong>), submitting an online admission 
                  application, completing payment via Paystack, or authenticating into the Learning Management System (LMS), you enter into a legally binding 
                  agreement governed by the laws of the Federal Republic of Nigeria and the Postgraduate Academic Regulations of Akwa Ibom State University.
                </p>
              </section>

              <section className="space-y-2">
                <h3 className="text-base font-black font-sc text-[#0B2644] border-l-4 border-[#7A470E] pl-3">
                  2. Executive User Accounts & Security Restrictions
                </h3>
                <p>
                  Every candidate and enrolled scholar is granted a single-user, non-transferable account. 
                  Under the university’s <strong>Role-Based Access Control (RBAC)</strong> and Least-Privilege Architecture:
                </p>
                <ul className="list-disc pl-5 space-y-1 text-slate-600">
                  <li>You must safeguard your assigned login credentials. You are strictly forbidden from sharing your access credentials with third-party research assistants, commercial dissertation writers, or corporate staff.</li>
                  <li>Any activity originating from your authenticated session is deemed your personal academic action.</li>
                  <li>The Business School maintains cryptographic security logging of all IP addresses, device identifiers, and assessment submission fingerprints to identify credential compromise.</li>
                </ul>
              </section>

              <section className="space-y-2">
                <h3 className="text-base font-black font-sc text-[#0B2644] border-l-4 border-[#7A470E] pl-3">
                  3. Intellectual Property Rights in Course Materials
                </h3>
                <p>
                  All lecture video masterclasses, Saunders' Research Onion instructional diagrams, case studies, continuous assessment rubrics, 
                  and proprietary curriculum notes are the exclusive intellectual property of Akwa Ibom State University and its distinguished faculty. 
                  Enrolled scholars are granted a limited, revocable, non-commercial educational license. 
                  <strong>Copying, rebroadcasting, publicly uploading to third-party study platforms, or commercially redistributing university courseware constitutes copyright infringement and will trigger immediate disciplinary action and civil litigation.</strong>
                </p>
              </section>

              <section className="space-y-2">
                <h3 className="text-base font-black font-sc text-[#0B2644] border-l-4 border-[#7A470E] pl-3">
                  4. Application Fees, Acceptance Fees & Refund Policy
                </h3>
                <p>
                  All payments processed via the integrated Paystack payment gateway are subject to clear statutory fiscal rules:
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                  <div className="p-3 bg-slate-50 rounded-xl border border-slate-200">
                    <strong className="text-slate-900 block font-sc">Application Processing Fee (₦25,000):</strong>
                    <span>Defrays administrative credential verification and admissions screening committee review. <em>This statutory fee is strictly non-refundable regardless of admission outcome.</em></span>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-xl border border-slate-200">
                    <strong className="text-slate-900 block font-sc">Provisional Acceptance Fee (₦50,000):</strong>
                    <span>Confirms acceptance of admission offer and reserves candidate quota in university gazette. Subject to Senate admission conditions.</span>
                  </div>
                </div>
              </section>

              <section className="space-y-2">
                <h3 className="text-base font-black font-sc text-[#0B2644] border-l-4 border-[#7A470E] pl-3">
                  5. Academic Integrity & Anti-Plagiarism Regulations
                </h3>
                <p>
                  As an executive scholar, you are held to the highest ethical benchmarks of scholarly inquiry:
                </p>
                <ul className="list-disc pl-5 space-y-1 text-slate-600">
                  <li><strong>Turnitin Verification:</strong> All research proposals, Continuous Assessment (CA) essays, term papers, and final dissertations must be processed through Turnitin with a maximum similarity index of <strong>less than 15%</strong>.</li>
                  <li><strong>Contract Cheating & Ghost-Writing:</strong> Commissioning third parties or commercial entities to compose academic work results in immediate expulsion and forfeiture of all accumulated credits.</li>
                  <li><strong>Ethical Conduct on Portal Forums:</strong> Discussion boards and collaborative forums must remain professional, respectful, and focused strictly on curriculum topics. Hate speech, commercial solicitations, and harassment trigger immediate portal ban.</li>
                </ul>
              </section>

              <section className="space-y-2">
                <h3 className="text-base font-black font-sc text-[#0B2644] border-l-4 border-[#7A470E] pl-3">
                  6. Service Availability & Maintenance Windows
                </h3>
                <p>
                  While the university strives to maintain 99.8% portal availability, scheduled server maintenance, database upgrades, 
                  or power grid contingency switchovers may occasionally cause temporary service interruptions. 
                  Scheduled maintenance windows are announced at least 48 hours in advance through the official announcement marquee.
                </p>
              </section>

              <section className="space-y-2">
                <h3 className="text-base font-black font-sc text-[#0B2644] border-l-4 border-[#7A470E] pl-3">
                  7. Governing Law and Arbitration
                </h3>
                <p>
                  These Terms are governed by and construed in accordance with the substantive laws of the Federal Republic of Nigeria. 
                  Any dispute arising from or related to the use of this portal or executive admission shall first be submitted to 
                  the <strong>Admissions and Student Disciplinary Committee of the University Senate</strong> for amicable administrative 
                  resolution prior to any legal proceedings in a court of competent jurisdiction within Akwa Ibom State.
                </p>
              </section>

            </div>
          </div>
        )}

        {/* ======================================================== */}
        {/* DOCUMENT 3: COOKIE NOTE & PREFERENCE MANAGER             */}
        {/* ======================================================== */}
        {activeTab === 'cookies' && (
          <div className="bg-white rounded-3xl p-6 sm:p-10 border-2 border-slate-200 shadow-md space-y-8 animate-in fade-in duration-200">
            
            <div className="border-b-2 border-slate-100 pb-5">
              <span className="text-xs font-black uppercase tracking-wider text-[#7A470E] font-sc">
                Digital Privacy & Session Management
              </span>
              <h2 className="text-2xl sm:text-3xl font-black font-sc text-[#0B2644] mt-1">
                Official Cookie Note & Tracking Disclosure
              </h2>
              <p className="text-xs text-slate-500 mt-1">
                Transparent information regarding local browser cookies, authentication tokens, and session persistence technologies.
              </p>
            </div>

            {/* Cookie Overview Summary */}
            <div className="p-4 rounded-2xl bg-amber-50/50 border border-amber-200 text-xs text-slate-700 leading-relaxed">
              Cookies and local storage tokens are lightweight data elements stored on your device when you navigate our executive portal. 
              They enable essential portal navigation, maintain your authenticated LMS session across modules, secure your Paystack checkout transactions, 
              and remember your display preferences.
            </div>

            {/* Interactive Cookie Preference Console */}
            <div className="p-6 rounded-2xl bg-slate-50 border-2 border-slate-200 space-y-5">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 pb-3">
                <div className="flex items-center space-x-2">
                  <Sliders className="w-4 h-4 text-[#7A470E]" />
                  <h3 className="font-black font-sc text-sm text-[#0B2644] uppercase tracking-wide">
                    Your Cookie Preferences Control Center
                  </h3>
                </div>
                <span className="text-[11px] text-slate-500 font-mono">Changes apply to this browser instance</span>
              </div>

              <div className="space-y-4 text-xs">
                
                {/* 1. Essential Cookies */}
                <div className="p-4 bg-white rounded-xl border border-slate-200 flex items-start justify-between gap-4">
                  <div className="space-y-1">
                    <div className="flex items-center space-x-2">
                      <strong className="text-slate-900 font-sc">1. Strictly Necessary & Security Cookies</strong>
                      <span className="px-2 py-0.2 bg-emerald-100 text-emerald-800 text-[10px] font-bold rounded font-mono">
                        Always Active (Statutory)
                      </span>
                    </div>
                    <p className="text-slate-600 text-[11px] leading-normal">
                      Required for session authentication, security CSRF validation, Paystack live checkout confirmation, and role access routing. 
                      Cannot be deactivated as the portal will fail to function securely without them.
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={true}
                    disabled
                    className="w-4 h-4 text-[#7A470E] rounded cursor-not-allowed mt-1"
                  />
                </div>

                {/* 2. Functional & Academic Progression Cookies */}
                <div className="p-4 bg-white rounded-xl border border-slate-200 flex items-start justify-between gap-4">
                  <div className="space-y-1">
                    <div className="flex items-center space-x-2">
                      <strong className="text-slate-900 font-sc">2. Functional & Courseware Progression Cookies</strong>
                      <span className="px-2 py-0.2 bg-blue-100 text-blue-800 text-[10px] font-bold rounded font-mono">
                        Recommended
                      </span>
                    </div>
                    <p className="text-slate-600 text-[11px] leading-normal">
                      Saves your active lecture video timestamp, practice note drafts, sidebar visibility preferences, and Silicon AI discussion history 
                      so you never lose your progress between study sessions.
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={cookiePreferences.functional}
                    onChange={(e) => setCookiePreferences(prev => ({ ...prev, functional: e.target.checked }))}
                    className="w-4 h-4 text-[#7A470E] rounded cursor-pointer mt-1 focus:ring-[#7A470E]"
                  />
                </div>

                {/* 3. Performance & LMS Telemetry Cookies */}
                <div className="p-4 bg-white rounded-xl border border-slate-200 flex items-start justify-between gap-4">
                  <div className="space-y-1">
                    <div className="flex items-center space-x-2">
                      <strong className="text-slate-900 font-sc">3. Performance & LMS Quality Analytics</strong>
                      <span className="px-2 py-0.2 bg-purple-100 text-purple-800 text-[10px] font-bold rounded font-mono">
                        Analytics
                      </span>
                    </div>
                    <p className="text-slate-600 text-[11px] leading-normal">
                      Measures bandwidth performance during video streaming, detects broken courseware assets, and monitors server load balancing across our Ikot Akpaden data servers.
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={cookiePreferences.analytics}
                    onChange={(e) => setCookiePreferences(prev => ({ ...prev, analytics: e.target.checked }))}
                    className="w-4 h-4 text-[#7A470E] rounded cursor-pointer mt-1 focus:ring-[#7A470E]"
                  />
                </div>

                {/* 4. Marketing & Admissions Outreach Cookies */}
                <div className="p-4 bg-white rounded-xl border border-slate-200 flex items-start justify-between gap-4">
                  <div className="space-y-1">
                    <div className="flex items-center space-x-2">
                      <strong className="text-slate-900 font-sc">4. Outreach & External Social Referrals</strong>
                      <span className="px-2 py-0.2 bg-slate-100 text-slate-700 text-[10px] font-bold rounded font-mono">
                        Optional
                      </span>
                    </div>
                    <p className="text-slate-600 text-[11px] leading-normal">
                      Allows integration with official university LinkedIn and Facebook event invitations and tracks incoming applicant referral sources.
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={cookiePreferences.marketing}
                    onChange={(e) => setCookiePreferences(prev => ({ ...prev, marketing: e.target.checked }))}
                    className="w-4 h-4 text-[#7A470E] rounded cursor-pointer mt-1 focus:ring-[#7A470E]"
                  />
                </div>

              </div>

              <div className="pt-2 flex flex-wrap items-center justify-between gap-3">
                <span className="text-[11px] text-slate-500">
                  {cookieSaved ? (
                    <span className="text-emerald-700 font-bold flex items-center space-x-1">
                      <Check className="w-3.5 h-3.5" />
                      <span>Preferences saved successfully to local browser storage!</span>
                    </span>
                  ) : (
                    "Cookies expire automatically after 90 days of inactivity."
                  )}
                </span>
                <button
                  type="button"
                  onClick={handleSaveCookiePreferences}
                  className="px-5 py-2.5 bg-[#7A470E] hover:bg-[#5C3306] text-white font-bold rounded-xl text-xs font-sc transition-all shadow-sm cursor-pointer flex items-center space-x-1.5"
                >
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  <span>Save Cookie Preferences</span>
                </button>
              </div>
            </div>

            {/* Detailed Cookie Inventory Table */}
            <div className="space-y-3">
              <h3 className="text-sm font-black font-sc text-[#0B2644] uppercase tracking-wider">
                Comprehensive Cookie Registry & Technical Inventory
              </h3>
              <div className="overflow-x-auto rounded-2xl border border-slate-200">
                <table className="w-full text-left text-xs text-slate-700 border-collapse">
                  <thead className="bg-slate-100 text-slate-900 font-sc text-[11px] uppercase tracking-wider border-b border-slate-200">
                    <tr>
                      <th className="p-3">Cookie Identifier</th>
                      <th className="p-3">Classification</th>
                      <th className="p-3">Domain Provider</th>
                      <th className="p-3">Purpose & Function</th>
                      <th className="p-3">Expiry</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 bg-white">
                    <tr>
                      <td className="p-3 font-mono font-bold text-slate-900">aksu_sess_token</td>
                      <td className="p-3"><span className="text-emerald-700 font-bold">Strictly Necessary</span></td>
                      <td className="p-3">businessschool.aksu.edu.ng</td>
                      <td className="p-3">Stores encrypted JWT token maintaining executive scholar authentication state across portal views.</td>
                      <td className="p-3 font-mono">Session / 24 Hours</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono font-bold text-slate-900">pstk_sec_ref</td>
                      <td className="p-3"><span className="text-emerald-700 font-bold">Payment Security</span></td>
                      <td className="p-3">checkout.paystack.com</td>
                      <td className="p-3">Verifies Paystack cryptographic clearance token for application and acceptance fee settlement.</td>
                      <td className="p-3 font-mono">1 Hour</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono font-bold text-slate-900">lms_module_state</td>
                      <td className="p-3"><span className="text-blue-700 font-bold">Functional</span></td>
                      <td className="p-3">businessschool.aksu.edu.ng</td>
                      <td className="p-3">Tracks student progress through DBA 801 video timestamps, active tabs, and Silicon AI inquiry memory.</td>
                      <td className="p-3 font-mono">90 Days</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono font-bold text-slate-900">aksu_cookie_consent</td>
                      <td className="p-3"><span className="text-emerald-700 font-bold">Compliance</span></td>
                      <td className="p-3">businessschool.aksu.edu.ng</td>
                      <td className="p-3">Records your explicit preferences regarding optional analytics and outreach cookies under the NDPA.</td>
                      <td className="p-3 font-mono">1 Year</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* How to Control Cookies in Major Browsers */}
            <div className="pt-2 border-t border-slate-200 space-y-2 text-xs text-slate-600">
              <strong className="block text-slate-900 font-sc text-sm">Managing Cookies Directly in Your Browser:</strong>
              <p>
                You can also configure your web browser to block or alert you about cookies. 
                Consult the official documentation for your browser: 
                <span className="font-semibold text-slate-800"> Google Chrome, Apple Safari, Mozilla Firefox, or Microsoft Edge</span>. 
                Please note that blocking essential cookies will disrupt secure access to your admission screening dockets and the Student LMS.
              </p>
            </div>

          </div>
        )}

      </div>
    </div>
  );
};
